import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

export type Resource = {
  id: string | null;
  name: string | null;
  alias: string | null;
  copy: string | null;
  active?: boolean;
  order?: number;
  type?: string | null;
  assigned_organization_ids?: string[];
  assigned_organizations_name?: string[];
};

export const createResource = (): Resource => ({
  id: null,
  name: null,
  alias: null,
  copy: null,
  type: 'portal',
  active: false,
  order: 10,
  assigned_organization_ids: [],
});

type ResourcesSliceType = {
  resources: Resource[];
  loadingResources: boolean;
  showEditResourceModal: boolean;
  loadingUpdateResource: boolean;
};

const initialState: ResourcesSliceType = {
  resources: [],
  loadingResources: false,
  showEditResourceModal: false,
  loadingUpdateResource: false,
};

export const fetchResources = createAsyncThunk<Resource[], undefined, AsyncThunkConfig>(
  'resources/fetchResources',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v3_resource', undefined, {
        signal: thunkAPI.signal,
      })) as Resource[];
      return response;
    } catch (e) {
      logError(e, ['resourcesSlice', 'fetchResources']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const updateResource = createAsyncThunk<string, Resource, AsyncThunkConfig>(
  'resources/updateResource',
  async (resource, thunkAPI) => {
    try {
      const response = (await axios.post('v3_resource_upsert', resource)) as string;
      if (response) {
        thunkAPI.dispatch(setShowEditResourceModal(false));
        thunkAPI.dispatch(fetchResources());
        notification.success({
          title: 'Resource updated',
          description: 'Success',
        });
        return response;
      } else {
        notification.error({
          title: 'Resource updated',
          description: 'Failure',
        });
        return '';
      }
    } catch (e) {
      logError(e, ['resourcesSlice', 'updateResource']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const resourcesSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    setShowEditResourceModal: (state, action: PayloadAction<boolean>) => {
      state.showEditResourceModal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResources.pending, (state, _action) => {
        state.loadingResources = true;
      })
      .addCase(fetchResources.fulfilled, (state, action) => {
        state.resources = action.payload;
        state.loadingResources = false;
      })
      .addCase(fetchResources.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingResources = false;
        }
      })
      .addCase(updateResource.pending, (state, _action) => {
        state.loadingUpdateResource = true;
      })
      .addCase(updateResource.rejected, (state, _action) => {
        state.loadingUpdateResource = false;
      })
      .addCase(updateResource.fulfilled, (state, _action) => {
        state.loadingUpdateResource = false;
      });
  },
});

export const { setShowEditResourceModal } = resourcesSlice.actions;
